<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps<{
  modelValue: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'clear'): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
}

function handleClear() {
  emit('update:modelValue', '');
  emit('clear');
  inputRef.value?.focus();
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.modelValue) {
    event.stopPropagation();
    handleClear();
  }
}

// Focus the search input when component mounts or help panel opens
watch(() => props.modelValue, () => {
  // Focus logic can be added here if needed
});
</script>

<template>
  <div class="help-search">
    <i class="bi bi-search search-icon"></i>
    <input
      ref="inputRef"
      type="text"
      class="search-input"
      placeholder="Search help..."
      :value="modelValue"
      @input="handleInput"
      @keydown="handleKeydown"
    />
    <button
      v-if="modelValue"
      class="clear-btn"
      @click="handleClear"
      title="Clear search"
    >
      <i class="bi bi-x"></i>
    </button>
  </div>
</template>

<style scoped>
.help-search {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
}

.search-input {
  width: 200px;
  padding: 6px 30px 6px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: #ffffff;
  transition: all 0.15s;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-btn {
  position: absolute;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: none;
  color: #9ca3af;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}

.clear-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.clear-btn i {
  font-size: 16px;
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .search-input {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .search-input::placeholder {
  color: #64748b;
}

[data-bs-theme="dark"] .search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

[data-bs-theme="dark"] .search-icon {
  color: #64748b;
}

[data-bs-theme="dark"] .clear-btn {
  color: #64748b;
}

[data-bs-theme="dark"] .clear-btn:hover {
  background: #334155;
  color: #94a3b8;
}
</style>
